import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Swal from 'sweetalert2';
import Choices from './../../../components/Choices.js';
import * as utils from '../../../components/utils.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import { handHygieneLabels } from '../../_label/hand_hygiene/hand_hygiene_labels.js';

// Enhanced TabellaOpportunita with local state management
const TabellaOpportunita = forwardRef(({ 
  index, 
  operatorIndex,
  initialData, 
  parsedConfig, 
  indicationColumnText, 
  actionColumnText,
  handleRemoveOpportunita,
  onDataChange
}, ref) => {
  const { t } = useTranslation();
  
  // Local state for this table
  const [selectedOpportunita, setSelectedOpportunita] = useState(initialData?.selectedOpportunita || []);
  const [selectedAzioni, setSelectedAzioni] = useState(initialData?.selectedAzioni || []);

  // Expose data collection method to parent
  useImperativeHandle(ref, () => ({
    getData: () => ({
      selectedOpportunita,
      selectedAzioni,
      OrderItem: index + 1
    })
  }));

  const opportunitaItems = [
    { id: 'bef-pat', label: t('befPat', { ns: 'labels' }) },
    { id: 'bef-asept', label: t('befAsept', { ns: 'labels' }) },
    { id: 'aft-b-f', label: t('aftBF', { ns: 'labels' }) },
    { id: 'aft-pat', label: t('aftPat', { ns: 'labels' }) },
    { id: 'aft-p-surr', label: t('aftPSurr', { ns: 'labels' }) },
  ];
  
  const actionItem = [
    { id: 'hr', label: t('hr', { ns: 'labels' }) },
    { id: 'hw', label: t('hw', { ns: 'labels' }) },
    { id: 'missed', label: t('missed', { ns: 'labels' }) },
  ];

  // Local toggle functions that only update local state
  const toggleOpportunita = (id) => {
    if (parsedConfig?.AllowMultipleIndications) {
      // Multiple selection mode - behave like checkboxes
      setSelectedOpportunita(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
    } else {
      // Single selection mode - behave like radio buttons
      setSelectedOpportunita(prev =>
        prev.includes(id) ? [] : [id]
      );
    }
    onDataChange();
  };

  const handleRadioClick = (id) => {
    // Allow deselection of radio buttons by checking if already selected
    setSelectedAzioni(prev =>
      prev.includes(id) ? [] : [id]
    );
    onDataChange();
  };

  const handleToggleGuanti = () => {
    if (!selectedAzioni.includes('missed')) return;
    setSelectedAzioni(prev => 
      prev.includes('gloving') 
        ? prev.filter(a => a !== 'gloving')
        : [...prev, 'gloving']
    );
    onDataChange();
  };

  return (
    <div className="table-responsive">
      <table className="table table-bordered text-center align-middle">
        <thead>
          <tr>
            <th colSpan="2" className="bg-warning p-2 text-sm blackText">
              <div className="d-flex justify-content-between align-items-center">
                <span className="mx-auto">OPP {index + 1}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleRemoveOpportunita(index)}
                  title="Rimuovi"
                >
                  <i className="fa fa-trash"></i>
                </button>
              </div>
            </th>
          </tr>
          <tr>
            <th className="text-sm">{indicationColumnText}</th>
            <th className="text-sm">{actionColumnText}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 text-start">
              {opportunitaItems.map(item => (
                <div key={item.id} className="form-check mb-1">
                  <input
                    type={parsedConfig?.AllowMultipleIndications ? "checkbox" : "radio"}
                    className="form-check-input"
                    id={`opp-${item.id}-${operatorIndex}-${index}`}
                    checked={selectedOpportunita.includes(item.id)}
                    {...(parsedConfig?.AllowMultipleIndications
                      ? { onChange: () => toggleOpportunita(item.id) }
                      : { onClick: () => toggleOpportunita(item.id) }
                    )}
                  />
                  <label className="form-check-label" htmlFor={`opp-${item.id}-${operatorIndex}-${index}`}>
                    {item.label}
                  </label>
                </div>
              ))}
            </td>
            <td className="p-3 text-start">
              {actionItem.map(x => (
                <div key={x.id} className="form-check mb-1">
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`azione-${x.id}-${operatorIndex}-${index}`}
                    checked={selectedAzioni.includes(x.id)}
                    onClick={() => handleRadioClick(x.id)}
                  />
                  <label className="form-check-label" htmlFor={`azione-${x.id}-${operatorIndex}-${index}`}>
                    {x.label}
                  </label>
                </div>
              ))}
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`action-gloving-${operatorIndex}-${index}`}
                  disabled={!selectedAzioni.includes('missed')}
                  checked={selectedAzioni.includes('gloving')}
                  onChange={handleToggleGuanti}
                />
                <label
                  className={`form-check-label ${!selectedAzioni.includes('missed') ? 'text-muted' : ''}`}
                  htmlFor={`action-gloving-${operatorIndex}-${index}`}
                >
                  {t('gloves', { ns: 'labels' })}
                </label>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

// Main component with local state and ref forwarding
const Observed_Opportunity = forwardRef(({ vocs, operator, operatorIndex, parsedConfig, onDataChange }, ref) => {
  // Local state for operator data
  const [code, setCode] = useState(operator.Code || '');
  const [categoryOperator, setCategoryOperator] = useState(operator.CategoryOperator || '');
  const [count, setCount] = useState(operator.Count || 1);
  const [type, setType] = useState(operator.Type || '');
  const [gender, setGender] = useState(operator.Gender || '');
  
  // Local state for opportunities with stable IDs
  const [opportunitaList, setOpportunitaList] = useState(() => {
    if (Array.isArray(operator.Opportunities) && operator.Opportunities.length > 0) {
      return utils.clone(operator.Opportunities).map((item, index) => ({
        ...item,
        _uniqueId: item._uniqueId || `opp_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    return [{ 
      selectedOpportunita: [], 
      selectedAzioni: [],
      _uniqueId: `opp_${Date.now()}_0_${Math.random().toString(36).substr(2, 9)}`
    }];
  });

  // Refs for collecting data from TabellaOpportunita components
  const tabellaRefs = useRef([]);

  const { t } = useTranslation();

  const itemTypeVoc = [
    { id: 'Internal', label: t('internal', { ns: 'labels' }) },
    { id: 'External', label: t('external', { ns: 'labels' }) }
  ];
  
  const genderVoc = [
    { id: 'M', label: t('man', { ns: 'labels' }) },
    { id: 'F', label: t('woman', { ns: 'labels' }) }
  ];

  // Add centralized translations
  const i18n = menu.getI18n();
  i18n.addResourceBundle('it', 'labels', handHygieneLabels.it, true, true);
  i18n.addResourceBundle('en', 'labels', handHygieneLabels.en, true, true);

  // Clean up refs when list length changes, but maintain stable references
  useEffect(() => {
    // Only trim refs if the array has actually shrunk
    if (tabellaRefs.current.length > opportunitaList.length) {
      tabellaRefs.current = tabellaRefs.current.slice(0, opportunitaList.length);
    }
  }, [opportunitaList.length]);

  // Expose data collection method to parent
  useImperativeHandle(ref, () => ({
    getData: () => {
      // Collect data from all TabellaOpportunita components
      const opportunities = tabellaRefs.current
        .filter(ref => ref)
        .map(ref => {
          const data = ref.getData();
          // Remove internal _uniqueId before sending to backend
          const { _uniqueId, ...cleanData } = data;
          return cleanData;
        });

      return {
        ...operator,
        Code: code,
        CategoryOperator: categoryOperator,
        Count: count,
        Type: type,
        Gender: gender,
        Opportunities: opportunities
      };
    }
  }));

  const handleTypeClick = (id) => {
    setType(prev => prev === id ? '' : id);
    onDataChange();
  };

  const handleGenderClick = (id) => {
    setGender(prev => prev === id ? '' : id);
    onDataChange();
  };

  // Handle category selection with local state
  const handleCategoryChange = (selected) => {
    const selectedItem = selected?.selected?.[0];
    setCode(selectedItem?.Code || '');
    setCategoryOperator(selectedItem?.Name || '');
    onDataChange();
  };

  // Add a new empty row to the opportunity list
  const handleAddOpportunita = () => {
    setOpportunitaList(prev => [
      ...prev,
      { 
        selectedOpportunita: [], 
        selectedAzioni: [],
        _uniqueId: `opp_${Date.now()}_${prev.length}_${Math.random().toString(36).substr(2, 9)}`
      }
    ]);
    onDataChange();
  };

  // Remove an opportunity row with confirmation
  const handleRemoveOpportunita = (indexToRemove) => {
    Swal.fire({
      title: t('areYouSure', { ns: 'labels' }),
      text: t('deletingMessage', { ns: 'labels', opp: indexToRemove + 1 }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#aaa',
      confirmButtonText: t('yesDelete', { ns: 'labels' }),
      cancelButtonText: t('cancel', { ns: 'labels' }),
    }).then(result => {
      if (result.isConfirmed) {
        setOpportunitaList(prev => prev.filter((_, index) => index !== indexToRemove));
        // Remove the corresponding ref to maintain alignment
        tabellaRefs.current.splice(indexToRemove, 1);
        
        Swal.fire({
          title: t('deleted', { ns: 'labels' }),
          text: t('deletedSuccesfully', { ns: 'labels' }),
          icon: 'success',
          timer: 1200,
          showConfirmButton: false,
        });
        
        onDataChange();
      }
    });
  };

  return (
    <div className="col-12 col-lg-6 col-xxl-3 mb-4">
      <div className="border border-secondary rounded p-3 bg-white shadow h-100">
        <div className="mb-3">
          <label htmlFor="categorySelect" className="form-label fw-bold">
            {t("profCategory", { ns: "labels" })}
          </label>
          <Choices
            voc={vocs}
            value="Code"
            text="Name"
            textDelegate={item => `${item.Code} - ${item.Name}`}
            className="form-control orange-select blackText"
            selected={vocs.find(x => x.Code === code) ? [vocs.find(x => x.Code === code)] : []}
            onChange={handleCategoryChange}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="code" className="form-label fw-bold">
            {t('code', { ns: 'labels' })}
          </label>
          <input
            type="text"
            id="code"
            className="form-control"
            value={code}
            readOnly
          />
        </div>

        <div className="mb-3">
          <label htmlFor="count" className="form-label fw-bold">
            {t('count', { ns: 'labels' })}
          </label>
          <input
            type="number"
            id="count"
            className="form-control orange-input"
            value={count}
            onChange={e => {
              setCount(parseInt(e.target.value || 1));
              onDataChange();
            }}
            min={1}
          />
        </div>

        {/* TYPE */}
        {parsedConfig?.ShowType && (
          <div className="mb-3">
            <label className="form-label fw-bold">{t('type', { ns: 'labels' })}</label>
            <div>
              {itemTypeVoc.map(item => (
                <div key={item.id} className="form-check form-check-inline me-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`type-${item.id}-${operatorIndex}`}
                    name={`type-${operatorIndex}`}
                    value={item.id}
                    checked={type === item.id}
                    onClick={() => handleTypeClick(item.id)}
                  />
                  <label className="form-check-label" htmlFor={`type-${item.id}-${operatorIndex}`}>
                    {item.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GENDER */}
        {parsedConfig?.ShowGender && (
          <div className="mb-3">
            <label className="form-label fw-bold">{t('gender', { ns: 'labels' })}</label>
            <div>
              {genderVoc.map(item => (
                <div key={item.id} className="form-check form-check-inline me-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`gender-${item.id}-${operatorIndex}`}
                    name={`gender-${operatorIndex}`}
                    value={item.id}
                    checked={gender === item.id}
                    onClick={() => handleGenderClick(item.id)}
                  />
                  <label className="form-check-label" htmlFor={`gender-${item.id}-${operatorIndex}`}>
                    {item.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {opportunitaList.map((item, index) => (
          <div key={item._uniqueId}>
            <TabellaOpportunita
              ref={el => {
                if (el) {
                  tabellaRefs.current[index] = el;
                }
              }}
              index={index}
              operatorIndex={operatorIndex}
              initialData={item}
              parsedConfig={parsedConfig}
              indicationColumnText={t('indication', { ns: 'labels' })}
              actionColumnText={t('handHygieneAction', { ns: 'labels' })}
              handleRemoveOpportunita={handleRemoveOpportunita}
              onDataChange={onDataChange}
            />
          </div>
        ))}

        <div className="mt-4 d-flex justify-content-center">
          <button
            className="btn btn-outline-warning"
            onClick={handleAddOpportunita}
          >
            {t('addOpportunity', { ns: 'labels' })}
          </button>
        </div>

        <div className="mt-4 pt-4 border-top border-dashed border-secondary d-flex justify-content-center">
          <div
            className="rounded-circle bg-primary"
            style={{ width: '8px', height: '8px' }}
          ></div>
        </div>
      </div>
    </div>
  );
});

export default Observed_Opportunity;
